
import React, { useEffect, useState } from 'react';
import { useCarouselControls } from '@/hooks/useCarouselControls';
import TrackItem from '@/components/TrackItem';
import { useFeaturedTracks } from '@/hooks/useFeaturedTracks';
import { Loader2, Music, Play, Pause } from 'lucide-react';
import { Button } from '@/components/ui/button';

const FeaturedTracksCarousel: React.FC = () => {
  const { 
    carouselRef, 
    isPaused,
    setCarouselSpeed,
    togglePause,
    handleMouseDown,
    handleTouchStart,
    handleMouseMove,
    handleTouchMove,
    handleDragEnd,
  } = useCarouselControls();
  
  const { tracks, isLoading, error, refreshTracks } = useFeaturedTracks();
  const [retryCount, setRetryCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Fetch all featured tracks
    console.log("FeaturedTracksCarousel: Attempting to fetch all featured tracks");
    refreshTracks();
  }, [refreshTracks, retryCount]);

  useEffect(() => {
    if (tracks.length > 0) {
      // Set the carousel speed based on the number of tracks
      // Add a delay to ensure proper measurement
      setTimeout(() => {
        setCarouselSpeed(tracks.length);
        setIsVisible(true); // Show carousel only after setup is complete
        console.log("FeaturedTracksCarousel: Tracks loaded successfully:", tracks.length, "tracks");
        console.log("FeaturedTracksCarousel: First track details:", JSON.stringify(tracks[0]));
      }, 300);
    } else if (!isLoading && retryCount < 3) {
      // Retry fetching if no tracks were found (up to 3 times)
      console.log("FeaturedTracksCarousel: No tracks found, retrying...");
      const timer = setTimeout(() => {
        setRetryCount(c => c + 1);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [tracks, setCarouselSpeed, isLoading, retryCount]);

  // Intersection Observer to detect when carousel is in viewport
  useEffect(() => {
    if (containerRef.current && !isLoading && tracks.length > 0) {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            // Delay to ensure proper loading
            setTimeout(() => {
              setIsVisible(true);
            }, 100);
            observer.disconnect();
          }
        },
        { threshold: 0.1 }
      );
      
      observer.observe(containerRef.current);
      
      return () => {
        observer.disconnect();
      };
    }
  }, [isLoading, tracks.length]);

  if (isLoading) {
    return (
      <div className="h-40 flex items-center justify-center">
        <Loader2 size={40} className="animate-spin opacity-50" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-40 flex flex-col items-center justify-center text-muted-foreground">
        <Music size={40} className="mb-2 opacity-50" />
        <p>Unable to load featured tracks: {error}</p>
        <button 
          onClick={() => setRetryCount(c => c + 1)} 
          className="mt-2 text-sm underline text-primary"
        >
          Try again
        </button>
      </div>
    );
  }

  if (tracks.length === 0) {
    return (
      <div className="h-40 flex flex-col items-center justify-center text-muted-foreground">
        <Music size={40} className="mb-2 opacity-50" />
        <p>No featured tracks available</p>
        <button 
          onClick={() => setRetryCount(c => c + 1)} 
          className="mt-2 text-sm underline text-primary"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="w-full bg-background overflow-hidden relative" ref={containerRef}>
      {/* Play/Pause Button */}
      <div className="absolute top-0 right-0 z-10 m-2">
        <Button 
          onClick={togglePause}
          variant="secondary" 
          size="icon" 
          className="rounded-full opacity-80 hover:opacity-100"
          aria-label={isPaused ? "Play carousel" : "Pause carousel"}
        >
          {isPaused ? <Play size={18} /> : <Pause size={18} />}
        </Button>
      </div>
      
      <div className={`carousel-container performance-boost ${isVisible ? 'opacity-100' : 'opacity-0'}`} 
           style={{ transition: 'opacity 0.5s ease-in-out' }}>
        <div 
          ref={carouselRef}
          className="infinite-carousel-track"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleDragEnd}
          onMouseLeave={handleDragEnd}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleDragEnd}
        >
          {/* First set of tracks */}
          {tracks.map((track, index) => (
            <TrackItem 
              key={`original-${track.id}-${index}`}
              track={{
                id: track.id,
                spotify_url: track.spotify_url,
                artist_name: track.artist || 'Unknown Artist',
                track_title: track.name || 'Unknown Track',
                created_at: new Date().toISOString()
              }} 
              prefix="original"
              index={index}
              className="flex-none"
            />
          ))}
          
          {/* Duplicate set of tracks for seamless looping */}
          {tracks.map((track, index) => (
            <TrackItem 
              key={`duplicate-${track.id}-${index}`}
              track={{
                id: track.id,
                spotify_url: track.spotify_url,
                artist_name: track.artist || 'Unknown Artist',
                track_title: track.name || 'Unknown Track',
                created_at: new Date().toISOString()
              }} 
              prefix="duplicate"
              index={index}
              className="flex-none"
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default FeaturedTracksCarousel;
